/**
 * Web Share API — payloads salientes (captura, ZIP, previews).
 */
import JSZip from 'jszip'
import { SETTINGS_SAVEAS } from '../constants/settings'
import {
  getCachedInstance,
  getCachedPreview,
  listCompleteSopsForStudy,
} from '../db/dicomCache'

export const SHARE_ZIP_WARN_BYTES = 50 * 1024 * 1024
export const SHARE_ZIP_BLOCK_BYTES = 100 * 1024 * 1024
export const SHARE_PREVIEW_MAX_FILES = 30
export const SHARE_PREVIEW_MAX_BYTES = 20 * 1024 * 1024
export const SHARE_PREVIEW_ZIP_THRESHOLD = 8

/** DICOM ya comprimido (lossy/lossless): STORE evita re-comprimir. */
export const ZIP_STORE_GENERATE = Object.freeze({
  type: 'blob',
  compression: 'STORE',
})

/**
 * @param {Blob|File} blob
 * @param {string} name
 * @param {string} [type]
 * @returns {File}
 */
export function blobToFile(blob, name, type) {
  const t = type || blob.type || 'application/octet-stream'
  try {
    return new File([blob], name, { type: t, lastModified: Date.now() })
  } catch (e) {
    // Safari viejo
    const f = blob
    f.name = name
    return f
  }
}

export function formatBytes(n) {
  const v = Number(n) || 0
  if (v < 1024) return `${v} B`
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export function zipSizeTier(byteLength) {
  const n = Number(byteLength) || 0
  if (n >= SHARE_ZIP_BLOCK_BYTES) return 'block'
  if (n >= SHARE_ZIP_WARN_BYTES) return 'warn'
  return 'ok'
}

/**
 * Limpia un segmento para nombre de archivo seguro en SO.
 * @param {string|null|undefined} value
 * @param {number} [maxLen]
 */
export function sanitizeFilenamePart(value, maxLen = 48) {
  return String(value || '')
    .trim()
    .replace(/\^/g, '_')
    .replace(/[^\w.\-()+ ]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, maxLen)
}

/**
 * @param {{
 *   patientName?: string,
 *   patientId?: string,
 *   studyDate?: string,
 *   modality?: string,
 *   studyInstanceUID?: string,
 * }} meta
 * @returns {string}
 */
export function buildStudyZipDownloadFilename(meta) {
  const m = meta || {}
  const patient = sanitizeFilenamePart(
    m.patientName || m.patientId || 'Estudio'
  )
  const date = sanitizeFilenamePart(m.studyDate || '')
  const modality = sanitizeFilenamePart(m.modality || '')
  const parts = [patient, date, modality].filter(Boolean)
  const base = parts.length
    ? parts.join('_')
    : sanitizeFilenamePart(m.studyInstanceUID || 'study')
  return `${base || 'study'}.zip`
}

/**
 * Nombre de ZIP para una sola serie.
 * @param {object} meta
 * @returns {string}
 */
export function buildSeriesZipDownloadFilename(meta) {
  const m = meta || {}
  const studyBase = buildStudyZipDownloadFilename(m).replace(/\.zip$/i, '')
  const sn =
    m.seriesNumber != null && String(m.seriesNumber).trim()
      ? `s${sanitizeFilenamePart(String(m.seriesNumber), 8)}`
      : ''
  const desc = sanitizeFilenamePart(
    m.seriesDescription || m.seriesLabel || '',
    32
  )
  const seriesPart = [sn, desc].filter(Boolean).join('_') || 'serie'
  return `${studyBase}_${seriesPart}.zip`
}

/**
 * Metadatos para nombre de ZIP desde stack o manifest.
 * @param {object[]} [files]
 * @param {object} [manifest]
 * @param {string} [studyUid]
 */
export function resolveStudyZipMeta(files, manifest, studyUid) {
  const list = Array.isArray(files) ? files : []
  const f0 = list.find((f) => f && (f.patient || f.study || f.series)) || list[0]
  const modality =
    (f0 && f0.series && f0.series.modality) ||
    (manifest && manifest.series && manifest.series[0] &&
      (manifest.series[0].modality || manifest.series[0].Modality)) ||
    (manifest && manifest.modality) ||
    ''
  return {
    patientName:
      (f0 && f0.patient && f0.patient.patientName) ||
      (manifest && manifest.patientName) ||
      '',
    patientId:
      (f0 && f0.patient && f0.patient.patientId) ||
      (manifest && manifest.patientId) ||
      '',
    studyDate:
      (f0 && f0.study && f0.study.studyDate) ||
      (manifest && manifest.studyDate) ||
      '',
    modality,
    studyInstanceUID:
      studyUid ||
      (f0 && f0.study && f0.study.studyId) ||
      (manifest && manifest.studyInstanceUID) ||
      '',
  }
}

/**
 * @returns {boolean}
 */
export function canUseWebShare() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
}

/**
 * @param {File[]} files
 * @returns {boolean}
 */
export function canShareFiles(files) {
  if (!canUseWebShare()) return false
  if (!files || !files.length) return false
  if (typeof navigator.canShare !== 'function') {
    // Algunos móviles soportan share con files sin canShare
    return true
  }
  try {
    return navigator.canShare({ files })
  } catch (e) {
    return false
  }
}

/**
 * Descarga un File como fallback cuando no hay Web Share.
 * @param {File|Blob} file
 * @param {string} [filename]
 */
export function downloadFileFallback(file, filename) {
  const name = filename || (file && file.name) || 'udv-share.bin'
  const url = URL.createObjectURL(file)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = name
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 2000)
  }
}

/**
 * @param {{ files?: File[], title?: string, text?: string, url?: string }} payload
 * @returns {Promise<{ ok: boolean, mode: 'share'|'download'|'abort'|'unavailable', error?: any }>}
 */
export async function shareOrFallback(payload) {
  const files = payload && Array.isArray(payload.files) ? payload.files : []
  const title = (payload && payload.title) || 'UDV'
  const text = (payload && payload.text) || ''
  const url = payload && payload.url

  if (files.length && canShareFiles(files)) {
    try {
      const data = { files, title }
      if (text) data.text = text
      await navigator.share(data)
      return { ok: true, mode: 'share' }
    } catch (err) {
      if (err && (err.name === 'AbortError' || err.name === 'NotAllowedError')) {
        return { ok: false, mode: 'abort', error: err }
      }
      // Fallback download
    }
  }

  if (!files.length && url && canUseWebShare()) {
    try {
      const data = { title, url }
      if (text) data.text = text
      if (typeof navigator.canShare !== 'function' || navigator.canShare(data)) {
        await navigator.share(data)
        return { ok: true, mode: 'share' }
      }
    } catch (err) {
      if (err && err.name === 'AbortError') {
        return { ok: false, mode: 'abort', error: err }
      }
    }
  }

  if (files.length) {
    files.forEach((f) => downloadFileFallback(f, f.name))
    return { ok: true, mode: 'download' }
  }

  if (url) {
    try {
      window.open(url, '_blank', 'noopener,noreferrer')
      return { ok: true, mode: 'download' }
    } catch (e) {
      return { ok: false, mode: 'unavailable', error: e }
    }
  }

  return { ok: false, mode: 'unavailable' }
}

function preferredCaptureType() {
  let type = (localStorage.getItem(SETTINGS_SAVEAS) || 'png').toLowerCase()
  if (type === 'jpg') type = 'jpeg'
  if (type !== 'png' && type !== 'jpeg') type = 'png'
  return type
}

/**
 * Extrae File desde el canvas del viewer (mismo criterio que captureViewport).
 * @param {object} viewer - instancia DicomViewer
 * @returns {Promise<File|null>}
 */
export async function buildCaptureFile(viewer) {
  if (!viewer || !viewer.dicomImage) return null
  if (typeof viewer.getCaptureBlob === 'function') {
    const got = await viewer.getCaptureBlob()
    if (got && got.blob) {
      return blobToFile(got.blob, got.filename || 'udv-capture.png', got.blob.type)
    }
  }

  const element = viewer.dicomImage
  const canvas =
    element.querySelector('canvas.cornerstone-canvas') ||
    element.querySelector('canvas')
  if (!canvas) return null

  let exportCanvas = canvas
  try {
    const out = document.createElement('canvas')
    out.width = canvas.width
    out.height = canvas.height
    const ctx = out.getContext('2d')
    if (ctx) {
      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, out.width, out.height)
      ctx.drawImage(canvas, 0, 0)
      exportCanvas = out
    }
  } catch (e) {
    exportCanvas = canvas
  }

  const type = preferredCaptureType()
  const mime = `image/${type}`
  const ext = type === 'jpeg' ? 'jpg' : 'png'
  const quality = type === 'jpeg' ? 0.92 : undefined

  const blob = await new Promise((resolve, reject) => {
    try {
      exportCanvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob vacío'))),
        mime,
        quality
      )
    } catch (err) {
      reject(err)
    }
  })

  const raw =
    viewer.filename ||
    (viewer.files &&
      viewer.files[viewer.sliceIndex] &&
      viewer.files[viewer.sliceIndex].name) ||
    'udv-capture'
  const base = String(raw).replace(/\.(dcm|dicom|jpe?g|png|webp)$/i, '') || 'udv-capture'
  const sliceTag =
    viewer.sliceIndex != null ? `-s${Number(viewer.sliceIndex) + 1}` : ''
  return blobToFile(blob, `${base}${sliceTag}.${ext}`, mime)
}

/**
 * @param {string} url
 * @returns {Promise<{ byteLength: number|null, ok: boolean }>}
 */
export async function probeRemoteZipSize(url) {
  if (!url || !/^https?:\/\//i.test(String(url))) {
    return { byteLength: null, ok: false }
  }
  try {
    const head = await fetch(url, { method: 'HEAD', mode: 'cors' })
    if (head.ok) {
      const cl = head.headers.get('content-length')
      if (cl != null && cl !== '') {
        const n = parseInt(cl, 10)
        if (Number.isFinite(n) && n >= 0) return { byteLength: n, ok: true }
      }
    }
  } catch (e) {
    /* CORS HEAD fallido — intentar Range */
  }
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Range: 'bytes=0-0' },
      mode: 'cors',
    })
    const cr = res.headers.get('content-range')
    if (cr) {
      const m = /\/(\d+)\s*$/.exec(cr)
      if (m) {
        const n = parseInt(m[1], 10)
        if (Number.isFinite(n)) return { byteLength: n, ok: true }
      }
    }
    const cl = res.headers.get('content-length')
    if (cl) {
      const n = parseInt(cl, 10)
      if (Number.isFinite(n)) return { byteLength: n, ok: true }
    }
  } catch (e2) {
    /* ignore */
  }
  return { byteLength: null, ok: false }
}

/**
 * @param {{ url?: string, filename?: string }} opts
 * @returns {Promise<{ file: File|null, byteLength: number|null, tier: string, error?: string }>}
 */
export async function buildStudyZipFromUrl(opts) {
  const url = opts && opts.url
  const filename = (opts && opts.filename) || 'study.zip'
  const allowLarge = !!(opts && opts.allowLarge)
  if (!url || !/^https?:\/\//i.test(String(url))) {
    return { file: null, byteLength: null, tier: 'block', error: 'no_url' }
  }

  const probe = await probeRemoteZipSize(url)
  const tier = zipSizeTier(probe.byteLength || 0)
  if (!allowLarge && probe.byteLength != null && tier === 'block') {
    return {
      file: null,
      byteLength: probe.byteLength,
      tier: 'block',
      error: 'too_large',
    }
  }

  try {
    const res = await fetch(url, { mode: 'cors' })
    if (!res.ok) {
      return {
        file: null,
        byteLength: probe.byteLength,
        tier: probe.byteLength != null ? tier : 'ok',
        error: `http_${res.status}`,
      }
    }
    const blob = await res.blob()
    const byteLength = blob.size
    const finalTier = zipSizeTier(byteLength)
    if (!allowLarge && finalTier === 'block') {
      return { file: null, byteLength, tier: 'block', error: 'too_large' }
    }
    return {
      file: blobToFile(blob, filename, blob.type || 'application/zip'),
      byteLength,
      tier: finalTier,
    }
  } catch (err) {
    return {
      file: null,
      byteLength: probe.byteLength,
      tier: probe.byteLength != null ? zipSizeTier(probe.byteLength) : 'ok',
      error: 'fetch_failed',
    }
  }
}

/**
 * ZIP local desde instancias DICOM en cache IDB (+ opc. ArrayBuffer en file.data).
 * @param {{ files?: object[], studyUid?: string, filename?: string, allowLarge?: boolean }} opts
 */
export async function buildStudyZipFromLocal(opts) {
  const files = (opts && opts.files) || []
  const filename = (opts && opts.filename) || 'study-local.zip'
  const allowLarge = !!(opts && opts.allowLarge)
  if (!files.length) {
    return { file: null, byteLength: null, tier: 'block', error: 'no_files' }
  }

  const zip = new JSZip()
  let added = 0
  for (let i = 0; i < files.length; i++) {
    const f = files[i]
    if (!f) continue
    const sop = f.sopInstanceUID ? String(f.sopInstanceUID) : null
    let bytes = null
    if (f.data instanceof ArrayBuffer) bytes = f.data
    else if (f.data && f.data.buffer) bytes = f.data.buffer
    else if (sop) {
      const row = await getCachedInstance(sop)
      if (row && row.blob) {
        bytes = await row.blob.arrayBuffer()
      }
    }
    if (!bytes || !bytes.byteLength) continue
    const name =
      (f.name && String(f.name).replace(/[^\w.\-]+/g, '_')) ||
      `${sop || `img-${i + 1}`}.dcm`
    zip.file(name.endsWith('.dcm') ? name : `${name}.dcm`, bytes)
    added += 1
  }

  if (!added) {
    return { file: null, byteLength: null, tier: 'block', error: 'no_cached' }
  }

  const blob = await zip.generateAsync({ ...ZIP_STORE_GENERATE })
  const byteLength = blob.size
  const tier = zipSizeTier(byteLength)
  if (!allowLarge && tier === 'block') {
    return { file: null, byteLength, tier: 'block', error: 'too_large' }
  }
  return {
    file: blobToFile(blob, filename, 'application/zip'),
    byteLength,
    tier,
    added,
    total: files.length,
  }
}

/**
 * ZIP del estudio completo desde dicom_cache (todas las SOP complete del studyUID).
 * @param {{ studyUid?: string, filename?: string, allowLarge?: boolean, expectedCount?: number }} opts
 */
export async function buildStudyZipFromIdb(opts) {
  const studyUid = opts && opts.studyUid ? String(opts.studyUid) : ''
  const filename = (opts && opts.filename) || 'study-local.zip'
  const allowLarge = !!(opts && opts.allowLarge)
  if (!studyUid) {
    return { file: null, byteLength: null, tier: 'block', error: 'no_study' }
  }

  const sopSet = await listCompleteSopsForStudy(studyUid)
  const sops = Array.from(sopSet)
  if (!sops.length) {
    return { file: null, byteLength: null, tier: 'block', error: 'no_cached' }
  }

  const expected =
    opts && opts.expectedCount != null ? Number(opts.expectedCount) || 0 : 0
  if (expected > 0 && sops.length < expected) {
    return {
      file: null,
      byteLength: null,
      tier: 'block',
      error: 'incomplete',
      added: sops.length,
      total: expected,
    }
  }

  const zip = new JSZip()
  let added = 0
  for (let i = 0; i < sops.length; i++) {
    const sop = sops[i]
    try {
      const row = await getCachedInstance(sop)
      if (!row || !row.blob) continue
      const bytes = await row.blob.arrayBuffer()
      if (!bytes || !bytes.byteLength) continue
      zip.file(`${sop}.dcm`, bytes)
      added += 1
    } catch (e) {
      /* skip SOP */
    }
  }

  if (!added) {
    return { file: null, byteLength: null, tier: 'block', error: 'no_cached' }
  }
  if (expected > 0 && added < expected) {
    return {
      file: null,
      byteLength: null,
      tier: 'block',
      error: 'incomplete',
      added,
      total: expected,
    }
  }

  const blob = await zip.generateAsync({ ...ZIP_STORE_GENERATE })
  const byteLength = blob.size
  const tier = zipSizeTier(byteLength)
  if (!allowLarge && tier === 'block') {
    return { file: null, byteLength, tier: 'block', error: 'too_large', added, total: sops.length }
  }
  return {
    file: blobToFile(blob, filename, 'application/zip'),
    byteLength,
    tier,
    added,
    total: sops.length,
  }
}

/**
 * @param {{
 *   url?: string,
 *   files?: object[],
 *   studyUid?: string,
 *   filename?: string,
 *   allowLarge?: boolean,
 *   expectedCount?: number,
 * }} opts
 */
export async function buildStudyZipFile(opts) {
  const url = opts && opts.url
  if (url && /^https?:\/\//i.test(String(url))) {
    const remote = await buildStudyZipFromUrl(opts)
    if (remote.file || remote.error === 'too_large') return remote
  }
  if (opts && Array.isArray(opts.files) && opts.files.length) {
    const fromFiles = await buildStudyZipFromLocal(opts)
    if (fromFiles.file || fromFiles.error === 'too_large') return fromFiles
  }
  if (opts && opts.studyUid) {
    return buildStudyZipFromIdb(opts)
  }
  return buildStudyZipFromLocal(opts)
}

/**
 * ZIP de una serie: IDB + fetch por dicomUrl/imageId si falta en caché.
 * @param {{ files?: object[], filename?: string }} opts
 */
export async function buildSeriesZipFromFiles(opts) {
  const files = (opts && opts.files) || []
  const filename = (opts && opts.filename) || 'series.zip'
  if (!files.length) {
    return { file: null, byteLength: null, tier: 'block', error: 'no_files' }
  }

  const zip = new JSZip()
  let added = 0
  for (let i = 0; i < files.length; i++) {
    const f = files[i]
    if (!f) continue
    let bytes = null
    if (f.data instanceof ArrayBuffer) bytes = f.data
    else if (f.data && f.data.buffer) bytes = f.data.buffer

    const sop = f.sopInstanceUID ? String(f.sopInstanceUID) : null
    if (!bytes && sop) {
      try {
        const row = await getCachedInstance(sop)
        if (row && row.blob) bytes = await row.blob.arrayBuffer()
      } catch (e) {
        /* ignore */
      }
    }

    if (!bytes || !bytes.byteLength) {
      const url =
        f.dicomUrl ||
        (f.imageId && String(f.imageId).replace(/^wadouri:/i, '')) ||
        null
      if (url && /^https?:\/\//i.test(String(url))) {
        try {
          const res = await fetch(String(url), { mode: 'cors' })
          if (res.ok) bytes = await res.arrayBuffer()
        } catch (e) {
          /* ignore */
        }
      }
    }

    if (!bytes || !bytes.byteLength) continue
    const name =
      (f.name && String(f.name).replace(/[^\w.\-]+/g, '_')) ||
      `${sop || `img-${i + 1}`}.dcm`
    zip.file(name.endsWith('.dcm') ? name : `${name}.dcm`, bytes)
    added += 1
  }

  if (!added) {
    return { file: null, byteLength: null, tier: 'block', error: 'no_cached' }
  }

  const allowLarge = !!(opts && opts.allowLarge)
  const blob = await zip.generateAsync({ ...ZIP_STORE_GENERATE })
  const byteLength = blob.size
  const tier = zipSizeTier(byteLength)
  if (!allowLarge && tier === 'block') {
    return { file: null, byteLength, tier: 'block', error: 'too_large' }
  }
  return {
    file: blobToFile(blob, filename, 'application/zip'),
    byteLength,
    tier,
    added,
    total: files.length,
  }
}

async function fetchPreviewBlob(file) {
  if (!file) return null
  const sop = file.sopInstanceUID ? String(file.sopInstanceUID) : null
  if (sop) {
    const cached = await getCachedPreview(sop)
    if (cached && cached.blob) return cached.blob
  }
  const url = file.previewUrl || (file.imageId && String(file.imageId).startsWith('http') ? file.imageId : null)
  if (url && /^https?:\/\//i.test(String(url))) {
    try {
      const res = await fetch(url, { mode: 'cors' })
      if (res.ok) return await res.blob()
    } catch (e) {
      /* ignore */
    }
  }
  return null
}

function previewExt(blob, file) {
  const t = (blob && blob.type) || ''
  if (/webp/i.test(t)) return 'webp'
  if (/png/i.test(t)) return 'png'
  if (/jpe?g/i.test(t)) return 'jpg'
  const u = String((file && file.previewUrl) || '')
  if (/\.webp/i.test(u)) return 'webp'
  if (/\.png/i.test(u)) return 'png'
  return 'jpg'
}

/**
 * @param {{ files: object[], mode: 'series'|'study', seriesUid?: string, studyUid?: string }} opts
 * @returns {Promise<{ files: File[], zipped: boolean, byteLength: number, truncated: boolean, error?: string }>}
 */
export async function buildPreviewShareFiles(opts) {
  const all = Array.isArray(opts && opts.files) ? opts.files : []
  const mode = (opts && opts.mode) || 'series'
  let list = all.filter((f) => f && (f.previewUrl || f.sopInstanceUID || f.imageId))
  if (mode === 'series' && opts.seriesUid) {
    const su = String(opts.seriesUid)
    list = list.filter((f) => {
      const uid =
        (f.seriesInstanceUID && String(f.seriesInstanceUID)) ||
        (f.seriesUID && String(f.seriesUID)) ||
        (f.series &&
          f.series.seriesInstanceUID &&
          String(f.series.seriesInstanceUID)) ||
        (f.series && f.series.seriesUID && String(f.series.seriesUID)) ||
        ''
      return uid === su
    })
  }

  const outBlobs = []
  let total = 0
  let truncated = false
  for (let i = 0; i < list.length; i++) {
    if (outBlobs.length >= SHARE_PREVIEW_MAX_FILES) {
      truncated = true
      break
    }
    const blob = await fetchPreviewBlob(list[i])
    if (!blob || !blob.size) continue
    if (total + blob.size > SHARE_PREVIEW_MAX_BYTES && outBlobs.length > 0) {
      truncated = true
      break
    }
    const ext = previewExt(blob, list[i])
    const sop = list[i].sopInstanceUID || `img-${i + 1}`
    const name = `preview-${String(sop).slice(-12)}.${ext}`
    outBlobs.push({ blob, name })
    total += blob.size
  }

  if (!outBlobs.length) {
    return { files: [], zipped: false, byteLength: 0, truncated: false, error: 'no_previews' }
  }

  const asFiles = outBlobs.map((x) => blobToFile(x.blob, x.name, x.blob.type))

  if (
    asFiles.length >= SHARE_PREVIEW_ZIP_THRESHOLD ||
    mode === 'study' ||
    total > SHARE_PREVIEW_MAX_BYTES / 2
  ) {
    const zip = new JSZip()
    asFiles.forEach((f) => zip.file(f.name, f))
    const zblob = await zip.generateAsync({ type: 'blob' })
    const zfile = blobToFile(
      zblob,
      mode === 'study' ? 'study-previews.zip' : 'series-previews.zip',
      'application/zip'
    )
    return {
      files: [zfile],
      zipped: true,
      byteLength: zblob.size,
      truncated,
    }
  }

  return { files: asFiles, zipped: false, byteLength: total, truncated }
}

/**
 * Bytes DICOM del SOP actual (cache o file.data).
 * @param {object} file
 * @returns {Promise<Uint8Array|null>}
 */
export async function getDicomBytesForFile(file) {
  if (!file) return null
  if (file.data instanceof ArrayBuffer) return new Uint8Array(file.data)
  if (file.data && file.data.buffer) {
    return new Uint8Array(file.data.buffer)
  }
  const sop = file.sopInstanceUID ? String(file.sopInstanceUID) : null
  if (!sop) return null
  const row = await getCachedInstance(sop)
  if (!row || !row.blob) return null
  const ab = await row.blob.arrayBuffer()
  return new Uint8Array(ab)
}
